import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import Header from '@/components/Header';
import { ROUTES } from '@/utils/constants';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/useAuthStore';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginForm = z.infer<typeof loginSchema>;

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { setFirebaseUser, setUserProfile } = useAuthStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const user = await authService.signIn(data.email, data.password);
      const profile = await authService.getUserProfile(user.uid);
      
      setFirebaseUser(user);
      if (profile) {
        setUserProfile(profile);
      }
      
      toast.success('Login successful!');
      navigate(ROUTES.DASHBOARD);
    } catch (error: any) {
      toast.error(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const user = await authService.signInWithGoogle();
      const profile = await authService.getUserProfile(user.uid);
      
      setFirebaseUser(user);
      if (profile) {
        setUserProfile(profile);
      }
      
      toast.success('Login successful!');
      navigate(ROUTES.DASHBOARD);
    } catch (error: any) {
      toast.error(error.message || 'Google sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="main-page-wrapper">
      <Header />
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '80vh', paddingTop: '80px' }}>
        <div className="card-style-one border-30 bg-white" style={{ maxWidth: '500px', width: '100%' }}>
          <h1 className="title-one mb-2">Welcome back</h1>
          <p className="text-dark mb-8">Sign in to your account</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="form-group mb-20">
              <label className="d-block mb-2 text-dark">Email</label>
              <input
                {...register('email')}
                type="email"
                className="form-control"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="text-danger fs-14 mt-1">{errors.email.message}</p>
              )}
            </div>

            <div className="form-group mb-20">
              <label className="d-block mb-2 text-dark">Password</label>
              <input
                {...register('password')}
                type="password"
                className="form-control"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="text-danger fs-14 mt-1">{errors.password.message}</p>
              )}
            </div>

            <button type="submit" className="btn-one w-100" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="d-flex align-items-center my-4">
            <div className="flex-fill border-top"></div>
            <span className="px-3 text-dark" style={{ fontSize: '14px' }}>OR</span>
            <div className="flex-fill border-top"></div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-100 d-flex align-items-center justify-content-center"
            style={{
              border: '1px solid #e0e0e0',
              backgroundColor: '#000000',
              color: '#ffffff',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            <svg
              className="me-2"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </button>

          <p className="text-center text-dark mt-6">
            Don't have an account?{' '}
            <Link to={ROUTES.SIGNUP} className="text-decoration-underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

